import { DocumentAccessLevel, DocumentStatus } from '@alucio/aws-beacon-amplify/src/models';
import type { DocumentVersionORM } from 'src/types/orms';
import type { GroupDraft } from 'src/components/SlideSelector/PageGroupList';
import type { ThumbnailPage } from 'src/components/SlideSelector/SlideSelector';
import type { PayloadGroup } from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import { getAssociatedParentIds, materializeDraftGroups } from './payload';
import { addAssociatedSlides } from './selection';
import { validatePayloadGroups, type ValidationDependencies } from './validation';
import { GroupStatus } from './groupStatus';

export interface AISearchSlideReference {
  id: string;
  page_number?: string;
}

export interface AISearchReferenceExclusion {
  reference: AISearchSlideReference;
  reason: string;
}

export interface ResolvedAISearchReferences {
  references: Required<AISearchSlideReference>[];
  exclusions: AISearchReferenceExclusion[];
  requestedCount: number;
}

const exclusion = (
  reference: AISearchSlideReference,
  reason: string,
): AISearchReferenceExclusion => ({ reference: { ...reference }, reason });

export const resolveAISearchDeckReferences = (
  references: readonly AISearchSlideReference[],
  versions: Record<string, DocumentVersionORM | undefined>,
): ResolvedAISearchReferences => {
  const canonicalReferences: Required<AISearchSlideReference>[] = [];
  const exclusions: AISearchReferenceExclusion[] = [];
  const seen = new Set<string>();

  references.forEach(reference => {
    const documentVersion = versions[reference.id];
    if (!documentVersion) {
      exclusions.push(exclusion(reference, 'Source version is unavailable'));
      return;
    }

    const pageNumber = Number(reference.page_number);
    if (!reference.page_number || !Number.isInteger(pageNumber)) {
      exclusions.push(exclusion(reference, 'Page number must be an integer'));
      return;
    }

    const page = documentVersion.meta.allPages.find(candidate => candidate.number === pageNumber);
    if (!page) {
      exclusions.push(exclusion(reference, 'Page does not exist in the cited version'));
      return;
    }

    const { accessLevel } = documentVersion.relations.documentORM.model;
    if (accessLevel !== DocumentAccessLevel.TENANT) {
      exclusions.push(exclusion(reference, 'Source document is not tenant content'));
      return;
    }
    if (documentVersion.model.status !== DocumentStatus.PUBLISHED) {
      exclusions.push(exclusion(reference, 'Source version is not published'));
      return;
    }
    if (documentVersion.relations.documentORM.model.status !== DocumentStatus.PUBLISHED) {
      exclusions.push(exclusion(reference, 'Source document is not published'));
      return;
    }
    if (!documentVersion.meta.version.isLatestPublished) {
      exclusions.push(exclusion(reference, 'Source version is not the latest published version'));
      return;
    }
    if (!documentVersion.meta.permissions.MSLSelectSlides) {
      exclusions.push(exclusion(reference, 'Source version does not allow slide selection'));
      return;
    }
    if (documentVersion.meta.permissions.MSLNonModifiable) {
      exclusions.push(exclusion(reference, 'Source version is non-modifiable'));
      return;
    }

    const key = `${reference.id}:${page.pageId}`;
    if (!seen.has(key)) {
      seen.add(key);
      canonicalReferences.push({ id: reference.id, page_number: String(page.number) });
    }
  });

  return {
    references: canonicalReferences,
    exclusions,
    requestedCount: canonicalReferences.length,
  };
};

interface BuildAISearchDeckDraftInput {
  references: readonly AISearchSlideReference[];
  versions: Record<string, DocumentVersionORM | undefined>;
  existingGroups: PayloadGroup[];
  associatedParentsMap: ReadonlyMap<string, boolean>;
  dependencies: ValidationDependencies;
}

interface AISearchDeckDraft extends ResolvedAISearchReferences {
  groups: PayloadGroup[];
  associatedParentsMap: Map<string, boolean>;
  addedCount: number;
  excludedCount: number;
}

const getLockedSourceGroup = (documentVersion: DocumentVersionORM, pageId: string) => {
  const pageORM = documentVersion.relations.pages.find(({ model }) => model.pageId === pageId);
  const pageGroup = pageORM?.relations.pageGroupORM;
  return pageGroup?.model.locked ? pageGroup : undefined;
};

const requestedDrafts = (
  documentVersion: DocumentVersionORM,
  sourcePages: ThumbnailPage[],
  references: readonly Required<AISearchSlideReference>[],
): GroupDraft[] => {
  const drafts: GroupDraft[] = [];
  const sourceGroups = new Set<string>();

  references.forEach(reference => {
    const page = sourcePages.find(candidate => candidate.number === Number(reference.page_number));
    if (!page) return;

    const sourceGroup = getLockedSourceGroup(documentVersion, page.pageId);
    if (sourceGroup) {
      if (sourceGroups.has(sourceGroup.model.id)) return;
      sourceGroups.add(sourceGroup.model.id);
      const pages = sourceGroup.relations.pages
        .map(({ model }) => sourcePages.find(candidate => candidate.pageId === model.pageId))
        .filter((candidate): candidate is ThumbnailPage => !!candidate);
      drafts.push({
        id: sourceGroup.model.id,
        name: sourceGroup.model.name,
        locked: true,
        pages,
      });
      return;
    }

    drafts.push({ id: page.pageId, pages: [page] });
  });

  return drafts;
};

export const buildAISearchDeckDraft = ({
  references,
  versions,
  existingGroups,
  associatedParentsMap,
  dependencies,
}: BuildAISearchDeckDraftInput): AISearchDeckDraft => {
  const resolved = resolveAISearchDeckReferences(references, versions);
  const exclusions = [...resolved.exclusions];
  const existingPageIds = new Set(existingGroups.flatMap(group => group.pages.map(page => page.pageId)));
  const acceptedByVersion = new Map<string, Required<AISearchSlideReference>[]>();

  resolved.references.forEach(reference => {
    const documentVersion = versions[reference.id]!;
    const page = documentVersion.meta.allPages.find(candidate => candidate.number === Number(reference.page_number))!;
    if (existingPageIds.has(page.pageId)) {
      exclusions.push(exclusion(reference, 'Requested slide is already in the deck'));
      return;
    }
    const sourceReferences = acceptedByVersion.get(reference.id) ?? [];
    sourceReferences.push(reference);
    acceptedByVersion.set(reference.id, sourceReferences);
  });

  const addedGroups: PayloadGroup[] = [];
  let associationMap = new Map(associatedParentsMap);

  acceptedByVersion.forEach((sourceReferences, versionId) => {
    const documentVersion = versions[versionId]!;
    const destinationGroups = [...existingGroups, ...addedGroups];
    const sourcePages: ThumbnailPage[] = documentVersion.meta.allPages.map(page => ({
      checked: false,
      disabled: false,
      isCover: false,
      ...page,
    }));
    const requested = requestedDrafts(documentVersion, sourcePages, sourceReferences);

    const firstAssociationPass = addAssociatedSlides(
      requested,
      sourcePages,
      destinationGroups,
      associationMap,
    );
    const selectedGroups = materializeDraftGroups(
      firstAssociationPass.groups,
      sourcePages,
      documentVersion,
      dependencies,
    );

    // Run the shared editor completion against only this source document. Existing
    // groups are treated as active context so an unrelated review state does not
    // impose a new admission rule on AI imports. The shared validator intentionally
    // expands links from required additions, including its existing one-hop behavior.
    const sourceContext = destinationGroups
      .filter(group => group.documentVersionORM?.model.id === versionId)
      .map(group => ({ ...group, groupStatus: GroupStatus.ACTIVE }));
    const validationInput = [...sourceContext, ...selectedGroups];
    const validated = validatePayloadGroups(validationInput, dependencies);
    const automaticGroups = validated.groups.slice(validationInput.length);
    const knownSourceGroups = new Set(validationInput.map(group => group.groupSrcId).filter(Boolean));
    const knownPages = new Set(validationInput.flatMap(group => group.pages.map(page => page.pageId)));
    const uniqueAutomaticGroups = automaticGroups.filter(group => {
      // Required single slides share the version ID as their source ID.
      const duplicate = group.pages.length === 1
        ? knownPages.has(group.pages[0].pageId)
        : group.groupSrcId && knownSourceGroups.has(group.groupSrcId);
      if (duplicate) return false;
      group.groupSrcId && knownSourceGroups.add(group.groupSrcId);
      group.pages.forEach(page => knownPages.add(page.pageId));
      return true;
    });

    const parentsByPage = getAssociatedParentIds(documentVersion.meta.allPages);
    const automaticGroupsWithParents = uniqueAutomaticGroups.map(group => ({
      ...group,
      pages: group.pages.map(page => {
        const parentIds = [...(parentsByPage.get(page.pageId) ?? [])];
        if (parentIds.length) associationMap.set(page.pageId, true);
        return { ...page, parentIds };
      }),
    }));
    associationMap = new Map([
      ...associationMap,
      ...firstAssociationPass.associatedParentsMap,
    ]);
    addedGroups.push(...selectedGroups, ...automaticGroupsWithParents);
  });

  return {
    groups: [...existingGroups, ...addedGroups],
    associatedParentsMap: associationMap,
    references: resolved.references,
    requestedCount: [...acceptedByVersion.values()].reduce((count, values) => count + values.length, 0),
    addedCount: addedGroups.reduce((count, group) => count + group.pages.length, 0),
    excludedCount: exclusions.length,
    exclusions,
  };
};
